import { axiom } from '../lib/axiom';
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Sentry } from '../lib/sentry';
import { TopBar } from '../components/dashboard/TopBar';
import { StreamList } from '../components/dashboard/StreamList';
import { StreamGraph } from '../components/dashboard/StreamGraph';
import { BranchHistoryPanel } from '../components/branching/BranchHistoryPanel';
import { EmptyStreamState } from '../components/onboarding/EmptyStreamState';
import { ConnectionLostBanner } from '../components/errors/ConnectionLostBanner';
import { HardRefreshModal } from '../components/errors/HardRefreshModal';
import { RateLimitWarning } from '../components/errors/RateLimitWarning';
import { SentryErrorFallback } from '../components/errors/SentryErrorFallback';
import type { VisibilityLevel, AccessMember } from '../components/stream';
import { useStreams } from '../contexts/StreamsContext';
import { useUserWebSocket } from '../contexts/UserWebSocketContext';
import { useAttention } from '../hooks/useAttention';
import { ModalProvider, useModal, useDeleteConfirmModal, useRestrictedAccessModal } from '../contexts/ModalContext';
import { ModalOrchestrator } from '../components/dashboard/ModalOrchestrator';
import { DroidButton } from '../components/chat';
import { motion, AnimatePresence } from 'framer-motion';
import { Wifi, WifiOff, Zap, Loader2, Edit2, Check, Flag } from 'lucide-react';
import { FeatureFlagsPanel } from '../components/dev/FeatureFlagsPanel';
import { GlassBoxPanel } from '../components/dashboard/GlassBoxPanel';
import { GlassBoxToggle } from '../components/dashboard/GlassBoxToggle';
import { useGlassBoxEvents } from '../hooks/useGlassBoxEvents';
import { loadEarlierCutoff } from '../lib/glassBoxCutoff';

// Helper to format relative time
function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
}

// Inner Dashboard component that uses modal context
function DashboardContent() {
  const {
    streams: apiStreams,
    loading,
    error,
    selectedStreamId,
    selectStream,
    createStream,
    updateStream,
    deleteStream,
    retryFetchStreams,
  } = useStreams();

  // WebSocket status for ConnectionLostBanner and HardRefreshModal
  const { status: wsStatus, error: wsError } = useUserWebSocket();

  // Modal context
  const { openModal, toggleModal, isModalOpen } = useModal();
  const deleteConfirm = useDeleteConfirmModal();
  const restrictedAccess = useRestrictedAccessModal();

  // Transform API streams to UI format
  const streams = useMemo(() => {
    return apiStreams.map((s) => ({
      id: s.id,
      name: s.name,
      status: s.status as 'active' | 'sleeping' | 'attention' | 'standby' | 'archived',
      progress: s.progress,
      lastUpdated: formatRelativeTime(s.updated_at),
      visibility: s.visibility as VisibilityLevel,
      accessList: [] as AccessMember[], // TODO: implement access list
      usage: s.usage,
      sandboxMachineType: s.sandbox_machine_type ?? null,
      sandboxMachineRegion: s.sandbox_machine_region ?? null,
    }));
  }, [apiStreams]);

  const selectedStream = selectedStreamId || '';
  const setSelectedStream = selectStream;

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  // Track per-stream selected node
  const [streamStates, setStreamStates] = useState<Record<string, {
    selectedNodeId: string | null;
  }>>({});

  // Stream name editing state
  const [isEditingStreamName, setIsEditingStreamName] = useState(false);
  const [editedStreamName, setEditedStreamName] = useState('');

  // Debug panel states (these toggle visibility, not true modals)
  const [showConnectionLost, setShowConnectionLost] = useState(false);
  const [showRateLimit, setShowRateLimit] = useState(false);
  const [showFeatureFlags, setShowFeatureFlags] = useState(false);

  // Get current stream state
  const currentStreamState = streamStates[selectedStream] || { selectedNodeId: null };

  // Update selected node for current stream
  const handleNodeSelect = useCallback((nodeId: string | null) => {
    setStreamStates(prev => ({
      ...prev,
      [selectedStream]: { selectedNodeId: nodeId }
    }));
  }, [selectedStream]);

  // Attention items from API
  const { items: attentionItems, count: attentionCount } = useAttention();

  // Glass Box observability (Spec 124 Phase 2)
  const {
    isOpen: isGlassBoxOpen,
    toggle: toggleGlassBox,
    filteredEvents: glassBoxEvents,
    filter: glassBoxFilter,
    setFilter: setGlassBoxFilter,
    clear: clearGlassBoxEvents,
    events: allGlassBoxEvents,
    loadEarlierEvents,
    hasFullHistory,
    isLoadingHistory,
  } = useGlassBoxEvents(selectedStream || null);

  // Handle navigation from attention panel
  const handleNavigateToAttentionItem = useCallback((streamId: string, nodeId: string) => {
    setSelectedStream(streamId);
    // Set node for the target stream (opens TaskChat in StreamGraph)
    setStreamStates(prev => ({
      ...prev,
      [streamId]: { selectedNodeId: nodeId }
    }));
    // Close attention panel
    toggleModal('attentionPanel');
  }, [setSelectedStream, toggleModal]);

  // Keyboard Shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        openModal('commandPalette');
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'b') {
        e.preventDefault();
        toggleModal('branchHistory');
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'l') {
        e.preventDefault();
        setShowConnectionLost(prev => !prev);
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'r') {
        e.preventDefault();
        setShowRateLimit(prev => !prev);
      }
      // Cmd/Ctrl+Shift+F for feature flags panel
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 'F') {
        e.preventDefault();
        setShowFeatureFlags(prev => !prev);
      }
      // Cmd/Ctrl+Shift+D for Glass Box panel
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 'D') {
        e.preventDefault();
        toggleGlassBox();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [openModal, toggleModal, toggleGlassBox]);

  const currentStream = streams.find(s => s.id === selectedStream) || streams[0];

  const handleCreateStream = useCallback(async (name: string, description: string) => {
    try {
      await createStream({ name, description });
    } catch (err) {
      axiom.error('Failed to create stream', { error: err instanceof Error ? err.message : String(err) });
    }
  }, [createStream]);

  const handleArchiveStream = useCallback(async (id: string) => {
    try {
      await updateStream(id, { status: 'archived' });
    } catch (err) {
      axiom.error('Failed to archive stream', { error: err instanceof Error ? err.message : String(err) });
    }
  }, [updateStream]);

  const handleToggleStreamActive = useCallback(async (id: string) => {
    const stream = streams.find(s => s.id === id);
    if (stream) {
      const newStatus = stream.status === 'sleeping' ? 'active' : 'sleeping';
      try {
        await updateStream(id, { status: newStatus });
      } catch (err) {
        axiom.error('Failed to toggle stream', { error: err instanceof Error ? err.message : String(err) });
      }
    }
  }, [streams, updateStream]);

  const handleDeleteStream = useCallback((id: string) => {
    const stream = streams.find(s => s.id === id);
    if (stream) {
      deleteConfirm.open(id, stream.name);
    }
  }, [streams, deleteConfirm]);

  const confirmDelete = useCallback(async () => {
    if (deleteConfirm.data) {
      try {
        await deleteStream(deleteConfirm.data.streamId);
      } catch (err) {
        axiom.error('Failed to delete stream', { error: err instanceof Error ? err.message : String(err) });
      }
    }
  }, [deleteConfirm.data, deleteStream]);

  const handleVisibilityChange = useCallback(async (id: string, visibility: VisibilityLevel) => {
    try {
      await updateStream(id, { visibility });
    } catch (err) {
      axiom.error('Failed to update visibility', { error: err instanceof Error ? err.message : String(err) });
    }
  }, [updateStream]);

  const handleRenameStream = useCallback(async (newName: string) => {
    if (selectedStream && newName.trim()) {
      try {
        await updateStream(selectedStream, { name: newName.trim() });
      } catch (err) {
        axiom.error('Failed to rename stream', { error: err instanceof Error ? err.message : String(err) });
      }
    }
  }, [selectedStream, updateStream]);

  const handleManageAccess = useCallback((id: string) => {
    const stream = streams.find(s => s.id === id);
    if (stream) {
      restrictedAccess.open(id, stream.name, stream.accessList || []);
    }
  }, [streams, restrictedAccess]);

  const handleAddMember = useCallback((email: string, role: AccessMember['role']) => {
    const currentData = restrictedAccess.data;
    if (currentData) {
      const newMember: AccessMember = {
        id: `m-${Date.now()}`,
        name: email.split('@')[0],
        email,
        role
      };
      // TODO: Implement access list persistence via API
      restrictedAccess.updateMembers([...currentData.members, newMember]);
    }
  }, [restrictedAccess]);

  const handleRemoveMember = useCallback((memberId: string) => {
    const currentData = restrictedAccess.data;
    if (currentData) {
      // TODO: Implement access list persistence via API
      restrictedAccess.updateMembers(currentData.members.filter(m => m.id !== memberId));
    }
  }, [restrictedAccess]);

  const handleUpdateMemberRole = useCallback((memberId: string, role: AccessMember['role']) => {
    const currentData = restrictedAccess.data;
    if (currentData) {
      // TODO: Implement access list persistence via API
      restrictedAccess.updateMembers(
        currentData.members.map(m => m.id === memberId ? { ...m, role } : m)
      );
    }
  }, [restrictedAccess]);

  const handleCreateBranch = useCallback((data: unknown) => {
    axiom.info('Creating branch', { data });
    toggleModal('branchModal');
  }, [toggleModal]);

  const showEmptyState = currentStream && currentStream.progress.total === 0;

  // Show loading state
  if (loading) {
    return (
      <div className="flex h-screen bg-[#0B0F17] text-text-primary items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 animate-spin text-primary-400" />
          <p className="text-text-secondary">Loading streams...</p>
        </div>
      </div>
    );
  }

  // Show error state with retry button
  if (error) {
    return (
      <div className="flex h-screen bg-[#0B0F17] text-text-primary items-center justify-center">
        <div className="flex flex-col items-center gap-4 text-center max-w-md">
          <p className="text-red-400 text-lg font-semibold">Failed to load streams</p>
          <p className="text-text-secondary text-sm">{error}</p>
          <button
            onClick={retryFetchStreams}
            className="mt-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-[#0B0F17] text-text-primary font-sans overflow-hidden">
      {/* Real WebSocket Connection Status - hide when hard-refresh modal is shown */}
      {wsError?.message !== 'hard-refresh-required' && (
        <ConnectionLostBanner status={wsStatus} error={wsError} />
      )}

      {/* Hard Refresh Modal - non-dismissible */}
      <HardRefreshModal isOpen={wsError?.message === 'hard-refresh-required'} />

      {/* Debug/Manual ConnectionLostBanner (toggle with Cmd+L) */}
      {showConnectionLost && <ConnectionLostBanner />}

      {/* Debug Controls - bottom left, subtle */}
      <div className="fixed bottom-4 left-4 z-40 flex gap-1 bg-surface-900/80 backdrop-blur border border-surface-700 rounded p-1 opacity-40 hover:opacity-100 transition-opacity">
        <button
          onClick={() => setShowConnectionLost(!showConnectionLost)}
          className={`p-1.5 rounded text-xs transition-all ${showConnectionLost ? 'bg-warning-500 text-text-primary' : 'text-text-muted hover:bg-surface-800 hover:text-text-secondary'}`}
          title="Toggle Connection Lost (Cmd+L)"
        >
          {showConnectionLost ? <WifiOff className="w-3 h-3" /> : <Wifi className="w-3 h-3" />}
        </button>
        <button
          onClick={() => setShowRateLimit(!showRateLimit)}
          className={`p-1.5 rounded text-xs transition-all ${showRateLimit ? 'bg-warning-500 text-text-primary' : 'text-text-muted hover:bg-surface-800 hover:text-text-secondary'}`}
          title="Toggle Rate Limit (Cmd+R)"
        >
          <Zap className="w-3 h-3" />
        </button>
        <button
          onClick={() => setShowFeatureFlags(!showFeatureFlags)}
          className={`p-1.5 rounded text-xs transition-all ${showFeatureFlags ? 'bg-amber-500 text-text-primary' : 'text-text-muted hover:bg-surface-800 hover:text-text-secondary'}`}
          title="Feature Flags (Ctrl+Shift+F)"
        >
          <Flag className="w-3 h-3" />
        </button>
      </div>

      {/* Feature Flags Panel */}
      {showFeatureFlags && (
        <FeatureFlagsPanel onClose={() => setShowFeatureFlags(false)} />
      )}

      <StreamList
        streams={streams}
        selectedId={selectedStream}
        onSelect={setSelectedStream}
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
        onNewStream={() => openModal('newStream')}
        onArchiveStream={handleArchiveStream}
        onToggleStreamActive={handleToggleStreamActive}
        onDeleteStream={handleDeleteStream}
        onVisibilityChange={handleVisibilityChange}
        onManageAccess={handleManageAccess}
      />

      <div className="flex-1 flex flex-col min-w-0">
        <TopBar
          onSearchClick={() => openModal('commandPalette')}
          onInboxClick={() => openModal('inbox')}
          onContextStoreClick={() => openModal('contextStore')}
          onSettingsClick={() => openModal('userPrefs')}
          onIntegrationsClick={() => openModal('integrations')}
          attentionCount={attentionCount}
          inboxCount={2}
          isWorkMode={false}
          onWorkModeToggle={() => openModal('attentionPanel')}
        />

        <main className="flex-1 min-h-0 relative flex overflow-hidden">
          <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
            <div className="h-14 border-b border-surface-600 flex items-center justify-between px-6 bg-surface-900/30">
              <div className="flex items-center gap-3">
                {isEditingStreamName ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={editedStreamName}
                      onChange={(e) => setEditedStreamName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleRenameStream(editedStreamName);
                          setIsEditingStreamName(false);
                        }
                        if (e.key === 'Escape') {
                          setIsEditingStreamName(false);
                        }
                      }}
                      className="bg-surface-800 border border-surface-500 rounded px-3 py-1 text-lg font-semibold focus:outline-none focus:ring-2 focus:ring-primary-500"
                      autoFocus
                    />
                    <button
                      onClick={() => {
                        handleRenameStream(editedStreamName);
                        setIsEditingStreamName(false);
                      }}
                      className="p-1.5 hover:bg-surface-800 rounded text-success-400"
                    >
                      <Check className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => {
                      setEditedStreamName(currentStream?.name || '');
                      setIsEditingStreamName(true);
                    }}
                    className="flex items-center gap-2 group"
                    disabled={!currentStream}
                  >
                    <h1 className="font-semibold text-lg">
                      {currentStream?.name || 'No stream selected'}
                    </h1>
                    {currentStream && (
                      <Edit2 className="w-4 h-4 text-text-muted opacity-0 group-hover:opacity-100 transition-opacity" />
                    )}
                  </button>
                )}
                {currentStream && (
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs font-medium border ${
                      currentStream.status === 'active'
                        ? 'bg-primary-500/10 text-primary-400 border-primary-500/20'
                        : currentStream.status === 'attention'
                          ? 'bg-warning-500/10 text-warning-400 border-warning-500/20'
                          : 'bg-purple-500/10 text-purple-400 border-purple-500/20'
                    }`}
                  >
                    {currentStream.status === 'active'
                      ? 'Active'
                      : currentStream.status === 'attention'
                        ? 'Needs Attention'
                        : 'Sleeping'}
                  </span>
                )}
                {/* Sandbox Backend Badge (Spec 124 Phase 0.5) */}
                {currentStream?.sandboxMachineType && (
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs font-medium border ${
                      currentStream.sandboxMachineType === 'standard_machine'
                        ? 'bg-primary-500/10 text-primary-400 border-primary-500/20'
                        : 'bg-purple-500/10 text-purple-400 border-purple-500/20'
                    }`}
                  >
                    {currentStream.sandboxMachineType === 'standard_machine' ? 'Fly' : 'Sprites'}
                    {currentStream.sandboxMachineRegion ? ` · ${currentStream.sandboxMachineRegion}` : ''}
                  </span>
                )}
              </div>
              {currentStream && (
                <div className="flex items-center gap-4">
                  <GlassBoxToggle
                    isOpen={isGlassBoxOpen}
                    eventCount={allGlassBoxEvents.length}
                    onClick={toggleGlassBox}
                  />
                  <button
                    onClick={() => toggleModal('branchHistory')}
                    className="text-sm text-text-secondary hover:text-text-primary transition-colors"
                  >
                    Branches
                  </button>
                  <button
                    onClick={() => openModal('streamSettings')}
                    className="text-sm text-text-secondary hover:text-text-primary transition-colors"
                  >
                    Settings
                  </button>
                  <button
                    onClick={() => openModal('exportModal')}
                    className="text-sm text-text-secondary hover:text-text-primary transition-colors"
                  >
                    Export
                  </button>
                  <div className="text-sm text-text-secondary">
                    Last updated {currentStream.lastUpdated}
                  </div>
                  <div className="flex -space-x-2">
                    {[1, 2, 3].map((i) => (
                      <div
                        key={i}
                        className="w-6 h-6 rounded-full bg-surface-700 border border-surface-700 flex items-center justify-center text-[10px]"
                      >
                        U{i}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {showRateLimit && (
              <div className="px-6 pt-4">
                <RateLimitWarning />
              </div>
            )}

            {showEmptyState ? (
              <EmptyStreamState streamId={selectedStream} />
            ) : currentStream ? (
              <Sentry.ErrorBoundary
                fallback={(props) => <SentryErrorFallback {...props} error={props.error as Error} />}
                beforeCapture={(scope) => {
                  scope.setTag('component', 'stream-graph');
                  scope.setContext('stream', {
                    id: selectedStream,
                    name: currentStream.name,
                  });
                }}
              >
                <StreamGraph
                  streamId={selectedStream}
                  streamName={currentStream.name}
                  selectedNodeId={currentStreamState.selectedNodeId}
                  onNodeSelect={handleNodeSelect}
                />
              </Sentry.ErrorBoundary>
            ) : null}

            <AnimatePresence>
              {isGlassBoxOpen && (
                <GlassBoxPanel
                  onClose={toggleGlassBox}
                  filteredEvents={glassBoxEvents}
                  filter={glassBoxFilter}
                  onFilterChange={setGlassBoxFilter}
                  onClear={clearGlassBoxEvents}
                  onLoadEarlier={() => {
                    // Use selected node if available, otherwise load for stream
                    const nodeId = streamStates[selectedStream ?? '']?.selectedNodeId;
                    if (nodeId && selectedStream) {
                      loadEarlierEvents(nodeId, loadEarlierCutoff(allGlassBoxEvents, nodeId, Date.now()));
                    }
                  }}
                  hasFullHistory={
                    streamStates[selectedStream ?? '']?.selectedNodeId
                      ? hasFullHistory(streamStates[selectedStream ?? '']?.selectedNodeId ?? '')
                      : true
                  }
                  isLoadingHistory={isLoadingHistory}
                />
              )}
            </AnimatePresence>
          </div>

          <AnimatePresence>
            {isModalOpen('branchHistory') && (
              <motion.div
                initial={{ x: 320 }}
                animate={{ x: 0 }}
                exit={{ x: 320 }}
                transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              >
                <BranchHistoryPanel />
              </motion.div>
            )}
          </AnimatePresence>
        </main>
      </div>

      {/* Modal Orchestrator - handles all modal rendering */}
      <ModalOrchestrator
        currentStreamName={currentStream?.name || ''}
        selectedStreamId={selectedStream}
        attentionItems={attentionItems}
        onCreateStream={handleCreateStream}
        onConfirmDelete={confirmDelete}
        onCreateBranch={handleCreateBranch}
        onNavigateToAttentionItem={handleNavigateToAttentionItem}
        onAddMember={handleAddMember}
        onRemoveMember={handleRemoveMember}
        onUpdateMemberRole={handleUpdateMemberRole}
      />

      {/* Bot Chat - Global assistant triggered by Droid button */}
      <DroidButton
        onClick={() => toggleModal('botChat')}
        isOpen={isModalOpen('botChat')}
        hasNotifications={attentionCount > 0}
      />

    </div>
  );
}

// Main Dashboard export with ModalProvider wrapper
export function Dashboard() {
  return (
    <ModalProvider>
      <DashboardContent />
    </ModalProvider>
  );
}
